<script>
import WebLayout from '@/layouts/Web/Layout.vue'

export default {
  layout: WebLayout,
}
</script>

<script setup>
import GwIcon from '@/components/GwIcon.vue'
import { computed, onMounted, nextTick } from 'vue'
import SEOHead from '@/components/SEOHead.vue'
import IncludedBackofficeCallout from '@/Pages/Web/components/IncludedBackofficeCallout.vue'
import { useWebTranslations } from '@/composables/useWebTranslations.js'
import { useScrollReveal } from '@/composables/useScrollReveal.js'

const { get, locale, webRoute, alternateLocales } = useWebTranslations()
const { refresh } = useScrollReveal()

const seoTitle = computed(() => get('pages.landing.seo_title'))
const seoDescription = computed(() => get('pages.landing.seo_description'))
const canonicalPath = computed(() => webRoute('web.landing-page'))
const pageAlternateLocales = computed(() => alternateLocales('web.landing-page'))

const featuresHref = computed(() => `${webRoute('web.features')}#website`)

const trustItems = ['fast_easy', 'templates', 'live_editing']

const highlightBar = [
  {
    key: 'design',
    accent: '#A855F7',
    accentSoft: 'rgba(168,85,247,0.12)',
    icon: 'globe',
  },
  {
    key: 'mobile',
    accent: '#22A06B',
    accentSoft: 'rgba(34,160,107,0.12)',
    icon: 'device-mobile',
  },
  {
    key: 'seo',
    accent: '#7C3AED',
    accentSoft: 'rgba(124,58,237,0.12)',
    icon: 'search',
  },
  {
    key: 'performance',
    accent: '#FF43A0',
    accentSoft: 'rgba(255,67,160,0.12)',
    icon: 'bolt',
  },
  {
    key: 'update',
    accent: '#F59754',
    accentSoft: 'rgba(245,151,84,0.14)',
    icon: 'cog',
  },
]

const featureCards = [
  {
    key: 'templates',
    accent: '#A855F7',
    accentSoft: 'rgba(168,85,247,0.12)',
    icon: 'pencil',
  },
  {
    key: 'editor',
    accent: '#F59754',
    accentSoft: 'rgba(245,151,84,0.14)',
    icon: 'photograph',
  },
  {
    key: 'customizable',
    accent: '#22A06B',
    accentSoft: 'rgba(34,160,107,0.12)',
    icon: 'hand',
  },
  {
    key: 'uptodate',
    accent: '#FF43A0',
    accentSoft: 'rgba(255,67,160,0.12)',
    icon: 'thumb-up',
  },
  {
    key: 'convert',
    accent: '#3B82F6',
    accentSoft: 'rgba(59,130,246,0.12)',
    icon: 'trending-up',
  },
]

const sidebarTools = [
  'sections', 'text', 'image', 'button', 'icon',
  'form', 'gallery', 'video', 'spacer', 'slides',
]

const editorItems = ['edit_text', 'edit_image', 'change_color', 'move_element', 'resize']

onMounted(() => {
  nextTick(() => refresh())
})
</script>

<template>
  <SEOHead
    :title="seoTitle"
    :description="seoDescription"
    :canonical="canonicalPath"
    :locale="locale"
    :alternate-locales="pageAlternateLocales"
    type="website"
  />

  <section class="relative overflow-hidden bg-[#F7F4FB]">
    <div aria-hidden="true" class="absolute inset-0 pointer-events-none">
      <div
        class="absolute -top-24 -right-20 w-[34rem] h-[34rem] rounded-full blur-3xl opacity-40"
        style="background: radial-gradient(circle, rgba(241,0,254,0.18) 0%, transparent 70%);"
      ></div>
      <div
        class="absolute -bottom-28 -left-24 w-[30rem] h-[30rem] rounded-full blur-3xl opacity-35"
        style="background: radial-gradient(circle, rgba(245,151,84,0.22) 0%, transparent 70%);"
      ></div>
    </div>

    <div class="relative max-w-[1280px] mx-auto px-6 lg:px-12 pt-10 pb-14 lg:pt-14 lg:pb-20">
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-start">

        <!-- Copy -->
        <div class="lg:col-span-5 reveal lg:pt-4">
          <span
            class="inline-flex items-center rounded-full px-3.5 py-1.5 text-[0.7rem] font-semibold tracking-[0.14em] uppercase text-white"
            style="background: linear-gradient(135deg, #A855F7 0%, #FF43A0 100%);"
          >
            {{ get('pages.landing.badge') }}
          </span>

          <h1 class="mt-5 text-[2.05rem] sm:text-[2.5rem] lg:text-[2.95rem] font-bold tracking-[-0.035em] leading-[1.12] text-[color:var(--gw-ink)] text-balance">
            {{ get('pages.landing.heading_before') }}
            <span class="text-[#FF43A0]">{{ get('pages.landing.heading_trust') }}</span>
            {{ get('pages.landing.heading_and') }}
            <span
              style="background: linear-gradient(135deg, #F59754 0%, #FF43A0 52%, #F100FE 100%); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;"
            >{{ get('pages.landing.heading_results') }}</span>
          </h1>

          <p class="mt-5 text-[1.05rem] leading-[1.65] text-[rgba(51,51,51,0.68)] max-w-md text-pretty">
            {{ get('pages.landing.lead') }}
          </p>

          <div class="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <a href="https://admin.growiyo.com" class="gw-btn-grad touch-manipulation justify-center">
              {{ get('home.hero.cta_start') }}
            </a>
            <a
              :href="webRoute('web.demo')"
              :aria-label="get('home.hero.cta_watch_aria')"
              class="inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-full bg-white text-[color:var(--gw-ink)] text-sm font-semibold border border-[rgba(51,51,51,0.12)] shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-[rgba(241,0,254,0.35)] touch-manipulation"
            >
              <span
                class="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                style="background: linear-gradient(135deg, #F59754 0%, #FF43A0 52%, #F100FE 100%);"
                aria-hidden="true"
              >
                <GwIcon name="play" class="w-3 h-3 text-white ml-0.5" />
              </span>
              {{ get('home.hero.cta_watch') }}
            </a>
          </div>

          <p class="mt-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[0.875rem] text-[rgba(51,51,51,0.62)]">
            <template v-for="(item, i) in trustItems" :key="item">
              <span class="inline-flex items-center gap-1.5">
                <GwIcon name="check" class="w-3.5 h-3.5 text-[#22A06B] shrink-0" />
                {{ get(`pages.landing.trust.${item}`) }}
              </span>
              <span v-if="i < trustItems.length - 1" class="text-[rgba(51,51,51,0.28)]" aria-hidden="true">•</span>
            </template>
          </p>
        </div>

        <!-- Visual stage: live editor -->
        <div
          class="lg:col-span-7 relative reveal reveal-delay-1 min-h-[380px] sm:min-h-[440px] lg:min-h-[500px]"
          aria-hidden="true"
        >
          <!-- Editor chrome -->
          <div class="absolute left-0 top-2 sm:top-0 w-[78%] sm:w-[74%] rounded-2xl overflow-hidden bg-white shadow-[0_32px_70px_-20px_rgba(51,51,51,0.40)] border border-[rgba(51,51,51,0.08)] z-10">
            <div class="flex items-center gap-1.5 px-3 py-2 border-b border-[rgba(51,51,51,0.06)] bg-[#FAFAFA]">
              <span class="w-2 h-2 rounded-full bg-[#FF5F57]"></span>
              <span class="w-2 h-2 rounded-full bg-[#FEBC2E]"></span>
              <span class="w-2 h-2 rounded-full bg-[#28C840]"></span>
              <span class="ml-2 flex-1 h-5 rounded-md bg-[rgba(51,51,51,0.05)] text-[0.6rem] text-[rgba(51,51,51,0.4)] flex items-center px-2">yourbrand.growiyo.com</span>
            </div>

            <div class="flex">
              <!-- Left tool sidebar -->
              <div class="hidden sm:flex w-[4.75rem] shrink-0 flex-col bg-[#1E1B2E] py-2.5 px-1.5 gap-0.5">
                <div
                  v-for="(tool, i) in sidebarTools"
                  :key="tool"
                  class="flex flex-col items-center gap-0.5 rounded-lg px-1 py-1.5"
                  :class="i === 1 ? 'bg-white/10' : ''"
                >
                  <span class="w-5 h-5 rounded-md flex items-center justify-center" :class="i === 1 ? 'bg-[#FF43A0]/20 text-[#FF43A0]' : 'text-white/55'">
                    <GwIcon name="menu" class="w-3 h-3" />
                  </span>
                  <span class="text-[0.45rem] leading-none text-center text-white/50 truncate w-full">
                    {{ get(`pages.landing.mock.tools.${tool}`) }}
                  </span>
                </div>
              </div>

              <!-- Canvas -->
              <div class="flex-1 min-w-0 bg-white">
                <div class="px-3 sm:px-4 py-2.5 flex items-center justify-between border-b border-[rgba(51,51,51,0.06)]">
                  <span class="text-[0.8rem] sm:text-[0.9rem] font-bold tracking-[-0.02em] text-[color:var(--gw-ink)]">YourBrand</span>
                  <div class="hidden md:flex items-center gap-3 text-[0.65rem] text-[rgba(51,51,51,0.5)]">
                    <span>Home</span><span>Services</span><span>About</span><span>Portfolio</span><span>Contact</span>
                  </div>
                  <span class="text-[0.6rem] sm:text-[0.65rem] font-semibold text-white bg-[color:var(--gw-ink)] px-2.5 py-1 rounded-md">
                    {{ get('pages.landing.mock.get_in_touch') }}
                  </span>
                </div>

                <div class="relative px-3 sm:px-5 py-5 sm:py-7 bg-gradient-to-br from-[#FAF8F7] via-[#F7F4FB] to-[#F3EEF9] min-h-[200px] sm:min-h-[240px]">
                  <!-- Formatting toolbar -->
                  <div class="absolute left-1/2 -translate-x-1/2 top-3 flex items-center gap-1 rounded-lg bg-white border border-[rgba(51,51,51,0.1)] shadow-md px-2 py-1 z-20">
                    <span v-for="n in 5" :key="n" class="w-5 h-5 rounded flex items-center justify-center text-[rgba(51,51,51,0.45)]">
                      <GwIcon name="lines" class="w-3 h-3" />
                    </span>
                    <span class="w-4 h-4 rounded-full" style="background: linear-gradient(135deg, #F59754, #F100FE);"></span>
                  </div>

                  <div class="relative mt-6 sm:mt-8 max-w-[85%]">
                    <p class="text-[0.95rem] sm:text-[1.15rem] font-bold tracking-[-0.02em] leading-snug text-[color:var(--gw-ink)] outline outline-2 outline-[#3B82F6] outline-offset-4 rounded-sm bg-white/40 px-1">
                      {{ get('pages.landing.mock.canvas_heading') }}
                    </p>
                  </div>

                  <div class="absolute right-3 sm:right-6 bottom-3 sm:bottom-5 flex items-end gap-2">
                    <div class="w-10 sm:w-12 h-14 sm:h-16 rounded-t-full bg-white border border-[rgba(51,51,51,0.08)] shadow-sm relative overflow-hidden">
                      <div class="absolute bottom-2 left-1/2 -translate-x-1/2 w-5 h-8 rounded-full bg-[#86EFAC]"></div>
                    </div>
                    <div
                      class="w-14 sm:w-16 h-14 sm:h-16 rounded-xl shadow-md"
                      style="background: linear-gradient(145deg, #C4B5FD 0%, #A78BFA 50%, #7C3AED 100%);"
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Live Editor floating panel -->
          <div class="absolute left-[8%] sm:left-[12%] bottom-16 sm:bottom-20 z-20 rounded-xl bg-white border border-[rgba(51,51,51,0.08)] shadow-[0_16px_40px_-12px_rgba(51,51,51,0.35)] px-2.5 py-2 w-[9.75rem] sm:w-[10.75rem]">
            <p class="px-2 pt-1 pb-1.5 text-[0.65rem] font-bold uppercase tracking-[0.12em] text-[rgba(51,51,51,0.45)]">
              {{ get('pages.landing.mock.live_editor') }}
            </p>
            <div
              v-for="(item, i) in editorItems"
              :key="item"
              class="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-[0.7rem] font-medium text-[color:var(--gw-ink)]"
              :class="i === 0 ? 'bg-[rgba(255,67,160,0.08)] text-[#FF43A0]' : ''"
            >
              <span
                class="w-1.5 h-1.5 rounded-full shrink-0"
                :style="{ background: i === 0 ? '#FF43A0' : 'rgba(51,51,51,0.25)' }"
              ></span>
              {{ get(`pages.landing.mock.editor.${item}`) }}
            </div>
          </div>

          <!-- Phone -->
          <div class="absolute right-0 sm:right-1 top-8 sm:top-10 w-[28%] sm:w-[24%] max-w-[140px] z-30">
            <div class="rounded-[1.35rem] border-[3px] border-[#1E1B2E] bg-white shadow-[0_24px_50px_-16px_rgba(51,51,51,0.5)] overflow-hidden">
              <div class="bg-[#1E1B2E] h-4 flex items-center justify-center">
                <span class="w-10 h-1 rounded-full bg-white/25"></span>
              </div>
              <div class="p-1.5">
                <div class="flex items-center justify-between px-1 py-1">
                  <span class="text-[0.55rem] font-bold text-[color:var(--gw-ink)]">YourBrand</span>
                  <span class="w-3.5 h-2 rounded-sm bg-[rgba(51,51,51,0.12)]"></span>
                </div>
                <div class="rounded-lg overflow-hidden bg-gradient-to-br from-[#FAF8F7] to-[#F3EEF9] p-2 min-h-[110px]">
                  <p class="text-[0.5rem] font-bold leading-snug text-[color:var(--gw-ink)]">
                    {{ get('pages.landing.mock.canvas_heading') }}
                  </p>
                  <div class="mt-2 flex items-end gap-1 justify-end">
                    <div class="w-5 h-8 rounded-t-full bg-white border border-[rgba(51,51,51,0.08)]"></div>
                    <div class="w-7 h-7 rounded-md" style="background: linear-gradient(145deg, #C4B5FD, #7C3AED);"></div>
                  </div>
                </div>
                <div class="mt-1.5 space-y-1 px-0.5">
                  <div class="h-1.5 rounded-full bg-[rgba(51,51,51,0.08)]"></div>
                  <div class="h-1.5 w-2/3 rounded-full bg-[rgba(51,51,51,0.06)]"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Highlight bar -->
      <div class="mt-10 lg:mt-12 reveal">
        <ul class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 list-none p-0 m-0 rounded-2xl bg-white/70 border border-[rgba(51,51,51,0.08)] px-3 sm:px-4 py-3 sm:py-3.5 shadow-[0_12px_32px_-20px_rgba(51,51,51,0.3)]">
          <li
            v-for="item in highlightBar"
            :key="item.key"
            class="flex items-center gap-2.5 px-2 py-1.5"
          >
            <span
              class="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
              :style="{ background: item.accentSoft, color: item.accent }"
            >
              <GwIcon :name="item.icon" class="w-4 h-4" />
            </span>
            <span class="text-[0.75rem] sm:text-[0.8rem] font-semibold tracking-[-0.01em] text-[color:var(--gw-ink)] leading-tight">
              {{ get(`pages.landing.highlights.${item.key}`) }}
            </span>
          </li>
        </ul>
      </div>

      <IncludedBackofficeCallout variant="landing" class="mt-8 lg:mt-10" />

      <!-- Feature cards -->
      <div class="mt-8 lg:mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-4 reveal">
        <article
          v-for="card in featureCards"
          :key="card.key"
          class="rounded-2xl bg-white border border-[rgba(51,51,51,0.08)] p-5 shadow-[0_12px_32px_-20px_rgba(51,51,51,0.35)] transition-transform duration-300 hover:-translate-y-1"
        >
          <span
            class="w-10 h-10 rounded-xl flex items-center justify-center"
            :style="{ background: card.accentSoft, color: card.accent }"
            aria-hidden="true"
          >
            <GwIcon :name="card.icon" class="w-5 h-5" />
          </span>
          <h2 class="mt-4 text-[0.95rem] font-bold tracking-[-0.015em] text-[color:var(--gw-ink)]">
            {{ get(`pages.landing.features.${card.key}.title`) }}
          </h2>
          <p class="mt-1.5 text-[0.8rem] leading-[1.55] text-[rgba(51,51,51,0.62)]">
            {{ get(`pages.landing.features.${card.key}.body`) }}
          </p>
        </article>
      </div>

      <!-- Devices note -->
      <div class="mt-10 lg:mt-12 flex flex-col sm:flex-row items-center justify-between gap-4 reveal">
        <div class="flex items-center gap-3 text-[rgba(51,51,51,0.55)]">
          <span class="flex items-center gap-2.5" aria-hidden="true">
            <GwIcon name="device-desktop" class="w-5 h-5 shrink-0" />
            <GwIcon name="device-tablet" class="w-5 h-5 shrink-0" />
            <GwIcon name="device-phone" class="w-5 h-5 shrink-0" />
          </span>
          <p class="text-[0.875rem] font-medium text-[color:var(--gw-ink)]">
            {{ get('pages.landing.devices_note') }}
          </p>
        </div>
        <a :href="featuresHref" class="gw-btn-ghost">
          {{ get('pages.landing.cta_features') }}
          <GwIcon name="arrow-right" class="w-4 h-4" />
        </a>
      </div>
    </div>
  </section>
</template>
